/**
 * RBAC access helpers — single source of truth for BOTH the sidebar
 * (DefaultAside) and the route guard (ContentRoutes).
 *
 * Grants come from `loginData.userAccessData`, which the login endpoint builds
 * from tblMapMenu joined to tblSubMenu/tblMenu. A page is identified by the
 * pair (menuName, subMenuName) — e.g. ("Reports", "Call Back") — and those
 * strings must match `menu.ts` `text` values exactly.
 *
 * Rules:
 *  - Level100 / Level4 (admin, sub-admin) bypass everything.
 *  - A role with NO grants at all keeps legacy behaviour (fail-safe: never
 *    lock a whole tenant out because grants were not seeded).
 *  - Otherwise a page is allowed only when granted with Isshow.
 */
import {
  masterPageMenu,
  operationPageMenu,
  reportsPageMenu,
} from "../../menu";

/** menuName as used in userAccessData -> the menu.ts group root. */
const GOVERNED_GROUPS: Array<{ menuName: string; group: any; rootKey: string }> =
  [
    { menuName: "Master", group: masterPageMenu, rootKey: "masterPages" },
    {
      menuName: "Operations",
      group: operationPageMenu,
      rootKey: "operationPages",
    },
    { menuName: "Reports", group: reportsPageMenu, rootKey: "reportsPages" },
  ];

export const isAdminLevel = (loginData: any): boolean => {
  const lvl = String(loginData?.UserLevel ?? loginData?.userLevel ?? "")
    .trim()
    .toLowerCase();
  return lvl === "level100" || lvl === "level4";
};

/** Parse userAccessData into rows, tolerating the string-encoded form. */
const grantRows = (loginData: any): any[] => {
  let rows: any = loginData?.userAccessData;
  if (typeof rows === "string" && rows.length) {
    try {
      rows = JSON.parse(rows);
    } catch {
      rows = [];
    }
  }
  return Array.isArray(rows) ? rows : [];
};

/**
 * Has this role been CONFIGURED at all — i.e. does it have any page rows,
 * regardless of whether they are switched on?
 *
 * This is the difference between "nobody has set this tenant up yet" (fall back
 * to showing everything, so a fresh tenant is never locked out) and "an admin
 * deliberately switched every page OFF" (show nothing). Previously both looked
 * identical because an all-off role produces an EMPTY grant set, so turning
 * everything off displayed the whole menu.
 */
export const hasGrantData = (loginData: any): boolean =>
  grantRows(loginData).some((r: any) => {
    const subs = r?.submenu ?? r?.Submenu ?? r?.subMenu;
    return Array.isArray(subs) && subs.length > 0;
  });

/** Set of "menuName||subMenuName" the role may SEE. */
export const buildGrantSet = (loginData: any): Set<string> => {
  const set = new Set<string>();
  let rows: any = loginData?.userAccessData;
  if (typeof rows === "string" && rows.length) {
    try {
      rows = JSON.parse(rows);
    } catch {
      rows = [];
    }
  }
  if (!Array.isArray(rows)) return set;
  rows.forEach((r: any) => {
    const menuName = String(r?.menuName ?? r?.MenuName ?? "").trim();
    const subs = r?.submenu ?? r?.Submenu ?? r?.subMenu;
    if (!menuName || !Array.isArray(subs)) return;
    subs.forEach((s: any) => {
      const subName = String(s?.subMenuName ?? s?.SubMenuName ?? "").trim();
      const show = s?.Isshow ?? s?.isshow ?? s?.IsShow ?? s?.isShow;
      const on = show === true || show === 1 || show === "1";
      if (subName && on) set.add(`${menuName}||${subName}`);
    });
  });
  return set;
};

/**
 * path -> { menuName, subMenuName } for every governed page, derived from
 * menu.ts so it can never drift from the sidebar.
 */
let pathIndex: Array<{
  path: string;
  menuName: string;
  subMenuName: string;
}> | null = null;

const getPathIndex = () => {
  if (pathIndex) return pathIndex;
  const idx: Array<{ path: string; menuName: string; subMenuName: string }> = [];
  GOVERNED_GROUPS.forEach(({ menuName, group, rootKey }) => {
    const sub = group?.[rootKey]?.subMenu;
    if (!sub) return;
    Object.values(sub).forEach((item: any) => {
      const p = String(item?.path ?? "").trim();
      const t = String(item?.text ?? "").trim();
      if (p && t) idx.push({ path: p.replace(/^\/+/, ""), menuName, subMenuName: t });
    });
  });
  // longest path first so "master-pages/branch" wins over "master-pages"
  idx.sort((a, b) => b.path.length - a.path.length);
  pathIndex = idx;
  return idx;
};

/** The governed page a pathname belongs to, or null if it isn't governed. */
export const resolvePage = (pathname: string) => {
  const clean = String(pathname ?? "").replace(/^\/+/, "").split("?")[0];
  if (!clean) return null;
  return (
    getPathIndex().find(
      (e) => clean === e.path || clean.startsWith(`${e.path}/`),
    ) ?? null
  );
};

/**
 * Can this user open this route?
 * Ungoverned routes (dashboard, profile, auth, 404) are always allowed.
 */
export const hasRouteAccess = (loginData: any, pathname: string): boolean => {
  if (isAdminLevel(loginData)) return true;
  const page = resolvePage(pathname);
  if (!page) return true; // not an access-controlled page
  // Fail-safe applies ONLY to a role that was never configured. A role whose
  // pages are all switched OFF has grant data, so it is enforced: no access.
  if (!hasGrantData(loginData)) return true;
  const grants = buildGrantSet(loginData);
  return grants.has(`${page.menuName}||${page.subMenuName}`);
};
